import type { FC } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './style.module.scss';
import { StyledInput } from './StyledInput';

// Lives on the marketing site, which is where the policy is authored and kept
// current — there is no in-app route for it.
const PRIVACY_POLICY_URL = 'https://www.risksmart.com/fineprint/privacy-policy';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isLoading: boolean;
}

export const ChatInput: FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  isLoading,
}) => {
  const { t } = useTranslation('common');
  const maxLength = 1000;
  const currentLength = value.length;
  const percentage = (currentLength / maxLength) * 100;

  // Determine character limit style based on percentage
  const getCharacterLimitClass = () => {
    if (percentage >= 95) {
      return `${styles.characterLimit} ${styles.danger}`;
    }
    if (percentage >= 80) {
      return `${styles.characterLimit} ${styles.warning}`;
    }

    return styles.characterLimit;
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };

  return (
    <div className={styles.inputContainer} data-testid={'chat-input-container'}>
      <StyledInput
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyPress}
        onSend={onSend}
        placeholder={'Type your message...'}
        disabled={isLoading}
        isLoading={isLoading}
        maxLength={maxLength}
      />
      <div className={getCharacterLimitClass()}>
        {currentLength}
        {'/'}
        {maxLength} {'characters'}
      </div>
      <div className={styles.footerText} data-testid={'chat-footer'}>
        {t('chat.disclaimer.text')}{' '}
        <a
          className={styles.footerLink}
          href={PRIVACY_POLICY_URL}
          target={'_blank'}
          rel={'noopener noreferrer'}
        >
          {t('chat.disclaimer.privacyPolicy')}
        </a>
      </div>
    </div>
  );
};
